import { ref } from 'vue';
import { createFetch } from '@vueuse/core';
import i18n from '../locales/i18n';
import { useArtistStore } from '@stores/artist.store';

const openstageApiFan = import.meta.env.VITE_OPENSTAGE_API_FAN;
const openstageApiFanQueue = import.meta.env.VITE_OPENSTAGE_API_FAN_QUEUE;

const baseFetcher = createFetch({
  baseUrl: openstageApiFan,
  options: {
    immediate: false,
    refetch: false,
    async beforeFetch({ options }) {
      options.headers = {
        ...options.headers,
        'Content-Type': 'application/json',
      };
      return { options };
    },
    onFetchError({ data, error }) {
      if (error?.name === 'AbortError') {
        return { error: { message: i18n.global.t('errors.requestAborted') } };
      }
      if (data?.error?.message?.includes('org.postgresql.util.PSQLException')) {
        return { error: { message: i18n.global.t('errors.couldNotConnectToServer') } };
      }
      if (data?.error?.message?.includes('could not decode token')) {
        return { error: { message: i18n.global.t('errors.wrongOrExpiredTokenUsed') } };
      }
      return {
        error: {
          message: data?.error?.message || data?.message || i18n.global.t('errors.defaultError'),
        },
      };
    },
  },
  fetchOptions: {
    mode: 'cors',
  },
});

const queueFetcher = createFetch({
  baseUrl: openstageApiFanQueue,
  options: {
    immediate: false,
    refetch: false,
    async beforeFetch({ options }) {
      options.headers = {
        ...options.headers,
        'Content-Type': 'application/json',
      };
      return { options };
    },
    onFetchError({ error }) {
      if (error?.name === 'AbortError') {
        return { error: { message: i18n.global.t('errors.requestAborted') } };
      }
      return { error: { message: i18n.global.t('errors.defaultError') } };
    },
  },
  fetchOptions: {
    mode: 'cors',
  },
});

type BaseResponse = {
  token?: string;
  error?: string;
};

type FetcherConfig<T extends object> = {
  endpoint: string;
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  requiresAuth?: boolean;
  requiresArtistId?: boolean;
  useQueueApi?: boolean;
  payloadAsQuery?: boolean;
  validatePayload?: (payload: T) => boolean;
};

export const createFetcher = <T extends object, R extends BaseResponse = BaseResponse>(
  config: FetcherConfig<T>,
) => {
  const artistStore = useArtistStore();
  const payload = ref<Partial<T>>({});
  const token = ref<string | undefined>(undefined);

  const validateArtistId = (): boolean => {
    if (config.requiresArtistId && !artistStore.id) {
      fetcher.error.value = i18n.global.t('errors.missingArtistId');
      return false;
    }
    return true;
  };

  const fetcher = (config.useQueueApi ? queueFetcher : baseFetcher)<R>(
    config.endpoint,
    { method: config.method || 'POST' },
    {
      beforeFetch({ url, options }) {
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };
        if (config.requiresAuth && token.value) {
          headers.Authorization = `Bearer ${token.value}`;
        }
        options.headers = headers;

        if (config.payloadAsQuery) {
          const queryParams = new URLSearchParams();
          Object.entries(payload.value as T).forEach(([key, value]) => {
            if (value !== undefined) queryParams.set(key, String(value));
          });
          if (config.requiresArtistId) {
            // throw?
            queryParams.set('artistId', artistStore.id);
          }
          const queryString = queryParams.toString();
          if (queryString) url = `${url}?${queryString}`;
        } else {
          const bodyPayload = { ...payload.value };
          if (config.requiresArtistId) {
            // throw?
            bodyPayload.artistId = artistStore.id;
          }
          options.body = JSON.stringify(bodyPayload);
        }

        return { url, options };
      },
    },
  ).json();

  return {
    data: fetcher.data,
    error: fetcher.error,
    isFetching: fetcher.isFetching,
    execute: async (newPayload: T, newToken?: string) => {
      if (!validateArtistId()) {
        return;
      }

      if (config.validatePayload && !config.validatePayload(newPayload)) {
        return;
      }

      payload.value = newPayload;
      token.value = newToken;
      await fetcher.execute();
    },
  };
};
